
import { useState, lazy, Suspense, useEffect } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/SEO";
import { ssrDocument } from "@/utils/ssrUtils";
import { createFAQSchema } from "@/utils/schemaUtils";
import "@/styles/global.css";

// Import critical sections eagerly
import HeroSection from "@/components/sections/HeroSection";
import FeaturesSection from "@/components/sections/FeaturesSection";

// Lazy load non-critical section components
const PricingSection = lazy(() => import("@/components/sections/PricingSection"));
const BenefitsSection = lazy(() => import("@/components/sections/BenefitsSection"));
const ComparisonSection = lazy(() => import("@/components/sections/ComparisonSection"));
const IDEsSection = lazy(() => import("@/components/sections/IDEsSection"));
const FAQSection = lazy(() => import("@/components/sections/FAQSection"));
const FeatureShowcaseSection = lazy(() => import("@/components/sections/FeatureShowcaseSection"));

// Invisible loading component that doesn't disrupt the layout
const SectionLoader = () => <div className="min-h-[1px]"></div>;

const Index = () => {
  const [activeFAQ, setActiveFAQ] = useState<string | null>(null);
  
  // Signal content readiness for pre-rendering and handle hash navigation
  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') return;
    
    // Handle hash navigation when coming from other pages
    const handleHashNavigation = () => {
      const hash = window.location.hash;
      if (hash) {
        const sectionId = hash.substring(1); // Remove the # symbol
        const section = ssrDocument.getElementById(sectionId);
        if (section) {
          // Immediate scroll for faster response
          section.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };
    
    // Handle hash on initial load immediately
    handleHashNavigation();
    
    // Quick retry for hash navigation in case elements weren't ready
    const quickRetry = setTimeout(() => {
      handleHashNavigation();
    }, 100);
    
    // Wait for all lazy components to load
    const timer = setTimeout(() => {
      // Dispatch custom event to signal content is ready
      window.dispatchEvent(new CustomEvent('content-ready'));
      
      // Also set a global flag for pre-rendering tools
      (window as any).__CONTENT_READY__ = true;
      
      // Final hash navigation attempt after content is ready
      handleHashNavigation();
    }, 1000); // Reduced from 2000ms to 1000ms
    
    return () => {
      clearTimeout(quickRetry);
      clearTimeout(timer);
    };
  }, []);
  
  // Enhanced structured data for rich snippets
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      // Product Schema
      {
        "@type": "Product",
        "@id": "https://coderide.ai/#product",
        "name": "CodeRide",
        "description": "Task Management Redesigned for AI. CodeRide eliminates the context reset cycle in AI coding, ensuring your assistant retains complete project understanding between sessions.",
        "brand": {
          "@id": "https://coderide.ai/#organization"
        },
        "category": "Developer Tools",
        "audience": {
          "@type": "Audience",
          "audienceType": "Software Developers"
        },
        "applicationCategory": "DeveloperApplication",
        "operatingSystem": "Web",
        "offers": [
          {
            "@type": "Offer",
            "name": "Explorer Plan",
            "price": "0",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock",
            "url": "https://coderide.ai/pricing",
            "description": "Perfect for exploring CodeRide and launch your first AI-optimized project. Includes 1 project and 40 AI-Enhanced Tasks (una tantum allocation).",
            "eligibleQuantity": {
              "@type": "QuantitativeValue",
              "value": "40",
              "unitText": "AI-Enhanced Tasks"
            },
            "priceSpecification": {
              "@type": "PriceSpecification",
              "price": "0",
              "priceCurrency": "EUR",
              "valueAddedTaxIncluded": true
            }
          },
          {
            "@type": "Offer",
            "name": "Creator Plan",
            "price": "9",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock",
            "url": "https://coderide.ai/pricing",
            "description": "Scale your AI coding across unlimited projects and zero context window problems. Includes 100 AI-Enhanced Tasks per month.",
            "eligibleQuantity": {
              "@type": "QuantitativeValue",
              "value": "100",
              "unitText": "AI-Enhanced Tasks per month"
            },
            "priceSpecification": {
              "@type": "PriceSpecification",
              "price": "9",
              "priceCurrency": "EUR",
              "billingIncrement": "P1M",
              "valueAddedTaxIncluded": true
            }
          },
          {
            "@type": "Offer",
            "name": "Pro Plan",
            "price": "29",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/InStock",
            "url": "https://coderide.ai/pricing",
            "description": "Professional-grade AI coding with unlimited projects and 400 AI-Enhanced Tasks per month for developing complex products.",
            "eligibleQuantity": {
              "@type": "QuantitativeValue",
              "value": "400",
              "unitText": "AI-Enhanced Tasks per month"
            },
            "priceSpecification": {
              "@type": "PriceSpecification",
              "price": "29",
              "priceCurrency": "EUR",
              "billingIncrement": "P1M",
              "valueAddedTaxIncluded": true
            }
          },
          {
            "@type": "Offer",
            "name": "Early Adopter Plan",
            "price": "119",
            "priceCurrency": "EUR",
            "availability": "https://schema.org/LimitedAvailability",
            "url": "https://coderide.ai/pricing",
            "description": "Lifetime access to all CodeRide features with unlimited projects and 2000 AI-Enhanced Tasks (una tantum allocation). Limited to 200 seats.",
            "eligibleQuantity": {
              "@type": "QuantitativeValue",
              "value": "2000",
              "unitText": "AI-Enhanced Tasks"
            },
            "priceSpecification": {
              "@type": "PriceSpecification",
              "price": "119",
              "priceCurrency": "EUR",
              "valueAddedTaxIncluded": true
            },
            "inventoryLevel": {
              "@type": "QuantitativeValue",
              "value": "200",
              "unitText": "seats available"
            }
          }
        ],
        "image": "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/coderide_dashboard-2.webp?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NvZGVyaWRlX2Rhc2hib2FyZC0yLndlYnAiLCJpYXQiOjE3NDUwNjExNTIsImV4cCI6MTc3NjU5NzE1Mn0.fiQCDIjdWtByoU3vFYwTmoFTqCQ7jXN74FMr291bKdY",
        "featureList": [
          "AI context preservation",
          "Persistent project memory",
          "Task optimization for AI agents",
          "MCP integration with popular IDEs",
          "Automated documentation analysis",
          "Context-aware task management"
        ],
        "applicationSuite": "AI Development Tools",
        "softwareVersion": "1.0.0-beta",
        "releaseNotes": "Beta version with core context management features"
      },
      // HowTo Schema for CodeRide Workflow
      {
        "@type": "HowTo",
        "@id": "https://coderide.ai/#howto",
        "name": "How to Use CodeRide for AI-Enhanced Development",
        "description": "Five simple steps to supercharge your AI coding experience with CodeRide's context management system.",
        "image": "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/coderide_dashboard-2.webp?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NvZGVyaWRlX2Rhc2hib2FyZC0yLndlYnAiLCJpYXQiOjE3NDUwNjExNTIsImV4cCI6MTc3NjU5NzE1Mn0.fiQCDIjdWtByoU3vFYwTmoFTqCQ7jXN74FMr291bKdY",
        "totalTime": "PT15M",
        "estimatedCost": {
          "@type": "MonetaryAmount",
          "currency": "USD",
          "value": "0"
        },
        "step": [
          {
            "@type": "HowToStep",
            "position": 1,
            "name": "Import Documentation",
            "text": "Upload your project documentation or task lists. CodeRide analyzes and automatically extracts actionable development tasks.",
            "image": "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/crd-062425_import_md.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9jOTljZjI2OC04OTEzLTRhYjktYmI4YS0yMTE1MjQ2YzRjNjQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NyZC0wNjI0MjVfaW1wb3J0X21kLm1wNCIsImlhdCI6MTc1MDg0MTc0NSwiZXhwIjoxNzgyMzc3NzQ1fQ.2a9Mz8SXGigdsH14Gbv1XETTIPxxA3GEFCOpAT8950g"
          },
          {
            "@type": "HowToStep",
            "position": 2,
            "name": "Optimize Tasks",
            "text": "CodeRide enhances each task with AI-optimized context, instructions, and project-specific details for your agent.",
            "image": "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/crd-062425_optimize_tasks.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9jOTljZjI2OC04OTEzLTRhYjktYmI4YS0yMTE1MjQ2YzRjNjQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NyZC0wNjI0MjVfb3B0aW1pemVfdGFza3MubXA0IiwiaWF0IjoxNzUwODQxODc1LCJleHAiOjE3ODIzNzc4NzV9.K07F7OCQmQy33t-M0DbUmZ8mMdcP4uAlOdhdD_QoSx4"
          },
          {
            "@type": "HowToStep",
            "position": 3,
            "name": "Launch Project",
            "text": "Initialize your project setup and prepare the development environment for AI agent execution.",
            "image": "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/crd-062425_start_project.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9jOTljZjI2OC04OTEzLTRhYjktYmI4YS0yMTE1MjQ2YzRjNjQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NyZC0wNjI0MjVfc3RhcnRfcHJvamVjdC5tcDQiLCJpYXQiOjE3NTA4NDE5NTQsImV4cCI6MTc4MjM3Nzk1NH0.tEwJ2-qfHtlx9UGGPzqvkPv-_OMp4i99qDsMRw-53As"
          },
          {
            "@type": "HowToStep",
            "position": 4,
            "name": "Start Coding",
            "text": "Your AI agent connects via MCP and begins autonomous development in your preferred IDE with complete project understanding.",
            "image": "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/crd-062425_mcp_start_project.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9jOTljZjI2OC04OTEzLTRhYjktYmI4YS0yMTE1MjQ2YzRjNjQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NyZC0wNjI0MjVfbWNwX3N0YXJ0X3Byb2plY3QubXA0IiwiaWF0IjoxNzUwODQxOTcxLCJleHAiOjE3ODIzNzc5NzF9.SPS-DwmsWB2eczkH73ND9wO3WWbk4Rhs6ScbRfC8HeM"
          },
          {
            "@type": "HowToStep",
            "position": 5,
            "name": "Keep Context Flowing",
            "text": "Your agent continuously retrieves and completes tasks while preserving all architectural decisions and coding patterns.",
            "image": "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/crd-062425_mcp_get_prompt.mp4?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9jOTljZjI2OC04OTEzLTRhYjktYmI4YS0yMTE1MjQ2YzRjNjQiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL2NyZC0wNjI0MjVfbWNwX2dldF9wcm9tcHQubXA0IiwiaWF0IjoxNzUwODQyMDA4LCJleHAiOjE3ODIzNzgwMDh9.kZ98r1NGvUmZOX2aPkyFrhAG0lvKizmBSFhxdi350A0"
          }
        ]
      },
      // Enhanced Organization
      {
        "@type": "Organization",
        "@id": "https://coderide.ai/#organization",
        "name": "CodeRide",
        "alternateName": "CodeRide AI",
        "url": "https://coderide.ai",
        "description": "CodeRide eliminates the context reset cycle in AI coding, ensuring your assistant retains complete project understanding between sessions.",
        "foundingDate": "2025",
        "email": "hello@coderide.ai",
        "logo": {
          "@type": "ImageObject",
          "url": "https://ideybnueizkxwqmjowpy.supabase.co/storage/v1/object/sign/coderide-website/Logo%20White&Color.svg?token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1cmwiOiJjb2RlcmlkZS13ZWJzaXRlL0xvZ28gV2hpdGUmQ29sb3Iuc3ZnIiwiaWF0IjoxNzQ0ODk3NzY5LCJleHAiOjE3NzY0MzM3Njl9.Mi0A6bu8o4zL0eHA01_QaALcR8Qo68Tk7DIxeYKq7Fk",
          "width": 200,
          "height": 60
        },
        "sameAs": [
          "https://x.com/coderide_ai"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "email": "hello@coderide.ai",
          "contactType": "customer service"
        },
        "founder": {
          "@type": "Organization",
          "name": "Pixdata",
          "legalName": "Pix Srl",
          "url": "https://pixdata.io",
          "description": "Digital acceleration company specializing in strategic consulting, software development and digital project management"
        }
      },
      // Service Schema
      {
        "@type": "Service",
        "@id": "https://coderide.ai/#service",
        "name": "AI Coding Context Management",
        "description": "Professional AI coding assistance with persistent context management for software development teams and individual developers.",
        "provider": {
          "@id": "https://coderide.ai/#organization"
        },
        "serviceType": "AI Development Tools",
        "areaServed": "Worldwide",
        "availableChannel": {
          "@type": "ServiceChannel",
          "serviceUrl": "https://coderide.ai",
          "serviceSmsNumber": null,
          "servicePhone": null
        },
        "category": "Software Development",
        "offers": [
          {
            "@type": "Offer",
            "availability": "https://schema.org/InStock",
            "url": "https://coderide.ai/pricing",
            "priceRange": "€0-€119"
          }
        ],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "CodeRide Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "AI Context Preservation",
                "description": "Maintain persistent project memory across AI coding sessions"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Task Optimization",
                "description": "AI-optimized task formatting for enhanced comprehension"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "IDE Integration",
                "description": "Seamless integration with popular AI coding environments"
              }
            }
          ]
        }
      },
      // BreadcrumbList Schema
      {
        "@type": "BreadcrumbList",
        "@id": "https://coderide.ai/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://coderide.ai/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Features",
            "item": "https://coderide.ai/#features"
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": "How it Works",
            "item": "https://coderide.ai/#how-it-works"
          },
          {
            "@type": "ListItem",
            "position": 4,
            "name": "FAQ",
            "item": "https://coderide.ai/#faq"
          }
        ]
      },
      // Website Schema
      {
        "@type": "WebSite",
        "@id": "https://coderide.ai/#website",
        "url": "https://coderide.ai/",
        "name": "CodeRide",
        "description": "Task Management Redesigned for AI - Stop explaining your project to AI over and over again",
        "publisher": {
          "@id": "https://coderide.ai/#organization"
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://coderide.ai/?q={search_term_string}"
          },
          "query-input": "required name=search_term_string"
        },
        "inLanguage": "en-US"
      },
      // WebPage
      {
        "@type": "WebPage",
        "@id": "https://coderide.ai/#webpage",
        "url": "https://coderide.ai/",
        "name": "CodeRide - Supercharge Your AI Coding Experience",
        "description": "Stop explaining your project to AI over and over again. CodeRide gives AI the context it needs to understand your codebase.",
        "isPartOf": {
          "@id": "https://coderide.ai/#website"
        },
        "about": {
          "@id": "https://coderide.ai/#product"
        },
        "publisher": {
          "@id": "https://coderide.ai/#organization"
        },
        "inLanguage": "en-US",
        "mainEntity": {
          "@id": "https://coderide.ai/#product"
        }
      },
      // FAQ Schema - use utility function
      createFAQSchema()
    ]
  };
  
  return (
    <div className="flex flex-col min-h-screen font-sans">
      <SEO 
        title="CodeRide - Task Management Redesigned for AI | Persistent Context for AI Coding"
        description="Stop explaining your project to AI over and over again. CodeRide eliminates context reset in AI coding with persistent project memory and optimized task management for AI assistants."
        canonical="https://coderide.ai/"
        keywords={[
          'AI coding assistant',
          'context management',
          'AI development tools',
          'persistent AI memory',
          'vibe coding',
          'AI code editor',
          'Cursor AI',
          'Windsurf IDE',
          'Cline AI',
          'MCP integration',
          'AI context preservation',
          'developer productivity',
          'AI task management',
          'code assistant',
          'AI programming'
        ]}
        entities={[
          'CodeRide',
          'AI',
          'artificial intelligence',
          'Cursor',
          'Windsurf',
          'Cline',
          'VS Code',
          'MCP',
          'Model Context Protocol',
          'GitHub Copilot',
          'Claude',
          'OpenAI',
          'software development',
          'programming',
          'developer tools'
        ]}
        topics={[
          'AI Development',
          'Context Management',
          'Developer Experience',
          'Software Engineering',
          'Programming',
          'Developer Productivity'
        ]}
        structuredData={structuredData}
      />
      <Header />
      
      <main className="flex-1">
        {/* Load critical sections eagerly for immediate visibility */}
        <HeroSection />
        <FeaturesSection />
        
        {/* Lazy load remaining sections with invisible fallbacks */}
        <Suspense fallback={<SectionLoader />}>
          <PricingSection />
        </Suspense>
        <Suspense fallback={<SectionLoader />}>
          <BenefitsSection />
        </Suspense>
        <Suspense fallback={<SectionLoader />}>
          <FeatureShowcaseSection />
        </Suspense>
        <Suspense fallback={<SectionLoader />}>
          <ComparisonSection />
        </Suspense>
        <Suspense fallback={<SectionLoader />}>
          <IDEsSection />
        </Suspense>
        <Suspense fallback={<SectionLoader />}>
          <FAQSection />
        </Suspense>
      </main>
      
      <Footer />
    </div>
  );
};

export default Index;
